// Centralized, crash-fast environment configuration for the web app.
//
// IMPORTANT:
// - Do NOT provide hardcoded fallbacks for required variables.
// - Missing variables should throw immediately at startup.

export type FirebaseWebConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
};

const requireEnv = (key: string): string => {
  const raw = (import.meta.env as Record<string, unknown>)[key];
  if (typeof raw !== "string" || raw.trim().length === 0) {
    throw new Error(`[env] Missing required environment variable: ${key}`);
  }
  return raw;
};

const requireBoolEnv = (key: string): boolean => {
  const v = requireEnv(key).trim().toLowerCase();
  if (v === "true") return true;
  if (v === "false") return false;
  throw new Error(
    `[env] ${key} must be either "true" or "false" (got: ${JSON.stringify(v)})`,
  );
};

const normalizeBaseUrl = (url: string): string => url.replace(/\/+$/, "");

const deriveDocsUrl = (apiBaseUrl: string): string => {
  // Common case: http://host:8000/api/v1 => http://host:8000/docs
  if (apiBaseUrl.endsWith("/api/v1")) {
    return `${apiBaseUrl.slice(0, -"/api/v1".length)}/docs`;
  }
  return `${apiBaseUrl}/docs`;
};

export const env = {
  isDev: import.meta.env.DEV,

  // Backend base URL (e.g. http://localhost:8000/api/v1)
  apiBaseUrl: normalizeBaseUrl(requireEnv("VITE_API_BASE_URL")),
  apiDocsUrl: "", // filled below

  // Whether we should connect to Firebase emulators (dev only in current app logic)
  useEmulator: requireBoolEnv("VITE_USE_EMULATOR"),

  mapboxToken: requireEnv("VITE_MAPBOX_TOKEN"),

  firebase: ((): FirebaseWebConfig => {
    // Only require production Firebase config if we are not using the dev emulator flow.
    const isDev = import.meta.env.DEV;
    const useEmulator = requireBoolEnv("VITE_USE_EMULATOR");

    if (isDev && useEmulator) {
      // In emulator mode we do not require real Firebase web credentials.
      // (The app will use a demo config from constants.)
      return {
        apiKey: "__EMULATOR__", // pragma: allowlist secret
        authDomain: "__EMULATOR__",
        projectId: "__EMULATOR__",
        storageBucket: "__EMULATOR__",
        messagingSenderId: "__EMULATOR__",
        appId: "__EMULATOR__",
      };
    }

    return {
      apiKey: requireEnv("VITE_FIREBASE_API_KEY"),
      authDomain: requireEnv("VITE_FIREBASE_AUTH_DOMAIN"),
      projectId: requireEnv("VITE_FIREBASE_PROJECT_ID"),
      storageBucket: requireEnv("VITE_FIREBASE_STORAGE_BUCKET"),
      messagingSenderId: requireEnv("VITE_FIREBASE_MESSAGING_SENDER_ID"),
      appId: requireEnv("VITE_FIREBASE_APP_ID"),
    };
  })(),
};

env.apiDocsUrl = deriveDocsUrl(env.apiBaseUrl);
